import { AnimatePresence, motion } from "motion/react";
import { useEffect } from "react";
import { motionEase, panelSpring, staggerContainer, staggerItem, uiSpring } from "../lib/motion";
import { useNexusStore } from "../store/nexusStore";

function destinationLabel(target: string) {
  try {
    return new URL(target, window.location.href).hostname.toUpperCase();
  } catch {
    return target.toUpperCase();
  }
}

export function PortalTransition() {
  const target = useNexusStore((state) => state.portalTarget);
  const clear = useNexusStore((state) => state.clearPortal);

  useEffect(() => {
    const resetTransition = () => clear();

    // A restored bfcache document keeps the module-level Zustand store alive.
    // Reset on both sides of navigation so Back never revives a finished transit.
    window.addEventListener("pagehide", resetTransition);
    window.addEventListener("pageshow", resetTransition);
    return () => {
      window.removeEventListener("pagehide", resetTransition);
      window.removeEventListener("pageshow", resetTransition);
    };
  }, [clear]);

  useEffect(() => {
    if (!target) return;
    const timer = window.setTimeout(() => window.location.assign(target), 920);
    return () => window.clearTimeout(timer);
  }, [target]);

  return (
    <AnimatePresence>
      {target && (
        <motion.div
          className="portal-transition"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={panelSpring}
        >
          <motion.div
            className="portal-transition__aperture"
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0.62, rotate: -18 }}
            animate={{ opacity: [0, 1, 0.72], scale: [0.62, 1, 1.12], rotate: 0 }}
            transition={{ duration: 0.88, ease: motionEase, times: [0, 0.42, 1] }}
          >
            <i />
            <i />
            <span />
          </motion.div>
          <motion.div
            className="portal-transition__axis"
            aria-hidden="true"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: [0, 0.7, 0] }}
            transition={{ duration: 0.86, ease: motionEase }}
          />
          <motion.div className="portal-transition__copy" variants={staggerContainer} initial="hidden" animate="show">
            <motion.span variants={staggerItem}>TRANSIT VECTOR LOCKED</motion.span>
            <motion.b variants={staggerItem}>{destinationLabel(target)}</motion.b>
            <motion.button
              variants={staggerItem}
              whileHover={{ scale: 1.025 }}
              whileTap={{ scale: 0.95 }}
              transition={uiSpring}
              onClick={clear}
            >
              CANCEL
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
